// The words of the System Admin area, the Permission matrix page and the console's Thresholds, Reminders and Templates
// areas (B10 slice S20b; CR-065, CR-066) that D0's label modules do not already carry. Every entry quotes his glossary
// or the vendored design-system excerpts verbatim; labels.test.mjs discovers this file (a lane folder of src/views) and
// checks each quote, its text and its status, and b10-words.test.mjs runs the lane's own checks over it. Words D0
// already defines (Thresholds, Reminders, Retry, Manual recovery, Reason, Actor, Timestamp, Evidence, Source, Validate,
// Ready, Validation Failed, the access words, the SLA tiers, the health bands) are imported by the screens, not repeated.

import { defineLabels, type LabelDef, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;

/** G §3, the Engineering / System Admin row: his two sentences. */
const SYSTEM_ADMIN_ROW = "\"Engineering/System Admin controls SSO, core security, audit engine and credentials.\"";
const SYSTEM_CONTROLLED = "\"SSO, permissions engine, audit engine, credentials remain system-admin controlled.\"";
/** G §15, the Audit row: his seven fields. */
const AUDIT_SENTENCE = "\"Audit is append-only: old/new, actor, timestamp, reason, related req/candidate, evidence and source.\"";
/** DS row 69 (`hist`): the audit row's keys. */
const AUDIT_KEYS = "*Old → New · Actor · Timestamp · Reason · Related req / candidate · Evidence · Source*";
/** DS row 120 (`cfgtbl`): the console's areas. */
const CFG_KINDS = "Statuses · Reason lists · Taxonomies · Thresholds · Reminders · Templates · Segments · Strategies · Effective version";
/** DS §2.1, the Error standard's disclosure rows. */
const ERROR_ROW = "with the one-line error, **Retry**, and an **Error details** disclosure (Correlation ID in mono · status · retries · error details · last attempt)";

/** The System Admin area: its name, its five sections and his sentence that keeps them system-admin controlled. */
export const SYSTEM_LABELS = /* @__PURE__ */ defineLabels({
  systemAdmin: his("System Admin"),
  permissionsEngine: his("Permissions engine"),
  auditEngine: his("Audit engine"),
  sso: his("SSO"),
  credentials: his("Credentials"),
  integrations: his(
    "Integrations"


),
  systemAdminControlled: his("SSO, permissions engine, audit engine, credentials remain system-admin controlled."),
  integration: his("Integration"),
  status: his("Status"),
  retries: his("Retries"),
  /** *Manual recovery* sits behind the matrix *Override* (DS row 82): his rule for every override. */
  overrideNeedsReason: his("Admin override only with reason and audit."),
});

/** The Audit engine: his sentence and the two audit-row keys D0's History labels do not carry. */
export const AUDIT_ENGINE_LABELS = /* @__PURE__ */ defineLabels({
  appendOnly: his("Audit is append-only: old/new, actor, timestamp, reason, related req/candidate, evidence and source."),
  oldNew: his("Old → New"),
  relatedReqCandidate: his("Related req / candidate"),
});

/** The console's three areas this slice adds, and the words of their editors. */
export const CONSOLE_AREA_LABELS = /* @__PURE__ */ defineLabels({
  templates: his("Templates"),
  template: his("Template"),
  reminderAndLimitedEscalation: his("Reminder and limited escalation"),
  noHmManagerEscalation: his("Do not auto-escalate to HM manager."),
  sla: his("SLA"),
  notification: his("Notification"),
});
